import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import WorkOrderDialog from "@/mechanic/components/WorkOrderDialog";
import { ArrowLeft, AlertTriangle, Plus, Wrench, Calendar, Gauge, Pencil } from "lucide-react";

const statusStyle = {
  "Open": "bg-amber-500/12 text-amber-400", "In Progress": "bg-blue-500/12 text-blue-400",
  "Awaiting Parts": "bg-purple-500/12 text-purple-400", "Completed": "bg-green-500/12 text-green-400",
};

export default function VehicleServicePage() {
  const { source, refId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [woOpen, setWoOpen] = useState(false);
  const [editingWo, setEditingWo] = useState(null);
  const [ovOpen, setOvOpen] = useState(false);
  const [ovDate, setOvDate] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["mech-vehicle", source, refId],
    queryFn: async () => (await api.get(`/vehicle/${source}/${refId}`)).data,
  });

  const overrideMut = useMutation({
    mutationFn: async () => api.put(`/service-meta/${source}/${refId}`, { last_oil_override: ovDate }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mech-vehicle", source, refId] }); qc.invalidateQueries({ queryKey: ["mech-rental"] }); toast.success("Last oil change updated"); setOvOpen(false); },
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-8">Vehicle not found.</div>;

  const { vehicle, summary, work_orders } = data;
  const preset = { source, ref_id: refId, label: vehicle.label };

  return (
    <div className="p-8" data-testid="mech-vehicle-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" />Back</button>

      {source === "rental" && summary.needs_service && (
        <div className="bg-red-600 text-white rounded-lg px-4 py-3 mb-4 flex items-center gap-2 font-semibold" data-testid="vehicle-needs-service-banner">
          <AlertTriangle className="h-5 w-5" /> NEEDS SERVICE — {summary.days_since_oil != null ? `${summary.days_since_oil} days since last oil change` : "no oil change on record"}
        </div>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl">{vehicle.label}</h1>
            <Badge className="border-0 bg-secondary text-foreground capitalize">{source}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono-data">{vehicle.plate || vehicle.stock_number || vehicle.vin || ""}</p>
        </div>
        <Button onClick={() => { setEditingWo(null); setWoOpen(true); }} data-testid="vehicle-new-wo"><Plus className="h-4 w-4 mr-2" />New Work Order</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Card className="p-4"><div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider"><Wrench className="h-4 w-4" />Total Spent</div><p className="text-2xl font-heading font-bold mt-1">{money(summary.total_spent)}</p><p className="text-xs text-muted-foreground">{summary.work_order_count} work order(s)</p></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider"><Calendar className="h-4 w-4" />Last Service</div><p className="text-2xl font-heading font-bold mt-1">{fmtDate(summary.last_service_date)}</p></Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider"><Gauge className="h-4 w-4" />Last Oil Change</div>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setOvDate(summary.oil_override || summary.last_oil_change || ""); setOvOpen(true); }} data-testid="edit-oil-override"><Pencil className="h-3.5 w-3.5" /></Button>
          </div>
          <p className="text-2xl font-heading font-bold mt-1">{fmtDate(summary.last_oil_change)}</p>
          {summary.oil_override && <p className="text-xs text-muted-foreground">(manual override)</p>}
        </Card>
      </div>

      <h2 className="font-heading text-xl mb-3">Service & Repair History</h2>
      {work_orders.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">No repairs logged yet for this vehicle.</Card>
      ) : (
        <div className="space-y-3">
          {work_orders.map((w) => (
            <Card key={w.id} className="p-4" data-testid={`history-wo-${w.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{w.complaint || "Service"}</p>
                    {w.is_oil_change && <Badge className="border-0 bg-secondary text-[10px]">OIL CHANGE</Badge>}
                    <Badge className={`border-0 ${statusStyle[w.status] || "bg-secondary"}`}>{w.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{fmtDate(w.service_date)} · {w.mechanic_name || "Unassigned"}{w.mileage ? ` · ${w.mileage} km` : ""}</p>
                  {w.diagnosis && <p className="text-sm mt-2">{w.diagnosis}</p>}
                  {(w.parts || []).length > 0 && (
                    <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
                      {w.parts.map((p, i) => <li key={i}>• {p.name} — {money(p.cost)}</li>)}
                    </ul>
                  )}
                  {w.notes && <p className="text-xs text-muted-foreground mt-2 italic">{w.notes}</p>}
                </div>
                <div className="text-right ml-4">
                  <p className="font-mono-data font-bold text-primary">{money(w.totals?.total)}</p>
                  <p className="text-[11px] text-muted-foreground">Parts {money(w.totals?.parts_total)} · Labor {money(w.totals?.labor_total)}</p>
                  <Button variant="ghost" size="sm" className="mt-1" onClick={() => { setEditingWo(w); setWoOpen(true); }} data-testid={`edit-history-wo-${w.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <WorkOrderDialog open={woOpen} onOpenChange={setWoOpen} editing={editingWo} presetVehicle={editingWo ? null : preset}
        onSaved={() => qc.invalidateQueries({ queryKey: ["mech-vehicle", source, refId] })} />

      <Dialog open={ovOpen} onOpenChange={setOvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Set Last Oil Change</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2">
            <Label className="label-mono">Last oil change date</Label>
            <Input type="date" value={ovDate} onChange={(e) => setOvDate(e.target.value)} data-testid="oil-override-date" />
            <p className="text-xs text-muted-foreground">Leave blank to clear the override and use the latest completed oil-change work order instead.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOvOpen(false)}>Cancel</Button><Button onClick={() => overrideMut.mutate()} data-testid="oil-override-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
